import { useReducer, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import DatePicker from 'react-datepicker'

import useDeviceSize from '@/hooks/useDeviceSize'

import { requestAllStorageFacilities } from '@/actions/operations/storageFacility'
import { requestAllShippingMethods } from '@/actions/operations/shippingMethod'
import { requestAllPackageUnits } from '@/actions/operations/packageUnit'

import {
  clearPackageUnitInventories,
  requestPackageUnitInventories
} from '@/actions/operations/packageUnitInventory'

import {
  createPackageUnitInventoryTransfer,
  requestPackageUnitInventoryTransfer,
  updatePackageUnitInventoryTransfer
} from '@/actions/operations/packageUnitInventoryTransfer'

import {
  getOperationsAllStorageFacilities,
  getOperationsAllShippingMethods,
  getOperationsAllPackageUnits,
  getOperationsPackageUnitInventories,
  getOperationsPackageUnitInventoryTransfer,
  getOperationsLoading,
  getOperationsError
} from '@/reducers/selectors'

import { Box, Flex, Label, Select, Text, Input, Loader } from '@/primitives'

import InputError from '@/elements/InputError'
import HeaderV2 from '@/components/Operations/Shared/HeaderV2'
import OrganizationInput from '../../../Shared/Organization/Input'

import { getValidationErrorMap } from '@/Util/GeneralUtils'

import history from '../../../../../history'
import Strings from '../Strings'

import { initialState, reducer, UPDATE_INPUT, SET_STATE } from './state'

import { INVENTORY_TRANSFER_STATUSES } from '../utils'
import { FIELDS, SCHEMA } from './config'

function PackageUnitInventoryTransferForm({ state, modulePath }) {
  const dispatch = useDispatch()
  const isMobile = useDeviceSize('mobile')
  const isLaptop = useDeviceSize()
  const { itemId } = useParams()

  const strings = Strings()

  const storageFacilities = getOperationsAllStorageFacilities()
  const shippingMethods = getOperationsAllShippingMethods()
  const packageUnits = getOperationsAllPackageUnits()
  const packageUnitInventories = getOperationsPackageUnitInventories()
  const packageUnitInventoryTransfer =
    getOperationsPackageUnitInventoryTransfer()

  const loading = getOperationsLoading()
  const error = getOperationsError()

  const [formState, dispatchFormState] = useReducer(reducer, initialState)

  const [errors, setErrors] = useState({})
  const [waiting, setWaiting] = useState(false)

  const inventoryTransferStatuses = INVENTORY_TRANSFER_STATUSES(strings)

  useEffect(() => {
    dispatch(clearPackageUnitInventories())
  }, [dispatch])

  useEffect(() => {
    dispatch(requestAllStorageFacilities())
    dispatch(requestAllShippingMethods())
    dispatch(requestAllPackageUnits())
  }, [dispatch])

  useEffect(() => {
    if (formState.organizationId && formState.fromStorageFacilityId) {
      dispatch(
        requestPackageUnitInventories({
          filter: {
            organizationId: [formState.organizationId],
            storageFacilityId: [formState.fromStorageFacilityId]
          }
        })
      )
    }
  }, [formState.organizationId, formState.fromStorageFacilityId])

  useEffect(() => {
    if (itemId && packageUnitInventoryTransfer.id !== itemId) {
      dispatch(
        requestPackageUnitInventoryTransfer({
          packageUnitInventoryTransferId: itemId
        })
      )
    }
  }, [itemId])

  useEffect(() => {
    if (
      packageUnitInventoryTransfer?.id &&
      packageUnitInventoryTransfer.id === itemId
    ) {
      const { packageUnitInventory, ...transfer } = packageUnitInventoryTransfer
      dispatchFormState({
        type: SET_STATE,
        state: {
          ...transfer,
          fromStorageFacilityId: packageUnitInventory.storageFacilityId,
          packageUnitId: packageUnitInventory.packageUnitId
        }
      })
    }
  }, [packageUnitInventoryTransfer])

  useEffect(() => {
    if (!itemId && state.organizations.length === 1) {
      dispatchFormState({
        type: UPDATE_INPUT,
        name: 'organizationId',
        value: state.organizations[0]
      })
    }
  }, [state.organizations])

  useEffect(() => {
    if (!itemId && state.fromStorageFacilities.length === 1) {
      dispatchFormState({
        type: UPDATE_INPUT,
        name: 'fromStorageFacilityId',
        value: state.fromStorageFacilities[0]
      })
    }
  }, [state.storageFacilities])

  useEffect(() => {
    if (!itemId && state.storageFacilities.length === 1) {
      dispatchFormState({
        type: UPDATE_INPUT,
        name: 'storageFacilityId',
        value: state.storageFacilities[0]
      })
    }
  }, [state.storageFacilities])

  useEffect(() => {
    if (waiting && !loading && !error) {
      dispatch(clearPackageUnitInventories())
      history.replace(modulePath)
    }
  }, [loading])

  useEffect(() => {
    if (formState.packageUnitId) {
      for (const packageUnitInventory of packageUnitInventories) {
        if (packageUnitInventory.packageUnit.id === formState.packageUnitId) {
          dispatchFormState({
            type: UPDATE_INPUT,
            name: 'packageUnitInventoryId',
            value: packageUnitInventory.id
          })
          return true
        }
      }
    }
    dispatchFormState({
      type: UPDATE_INPUT,
      name: 'packageUnitInventoryId',
      value: null
    })
  }, [formState.packageUnitId, packageUnitInventories])

  const onSubmit = async e => {
    try {
      const payload = await SCHEMA().validate(formState, { abortEarly: false })
      setWaiting(true)

      if (itemId) {
        payload.packageUnitInventoryTransferId = itemId
        dispatch(updatePackageUnitInventoryTransfer(payload))
      } else {
        dispatch(createPackageUnitInventoryTransfer(payload))
      }
    } catch (error) {
      setErrors(getValidationErrorMap(error))
    }
  }

  const handleInput = e => {
    const { name, value } = e.currentTarget
    dispatchFormState({ type: UPDATE_INPUT, name, value })
  }

  const handleNumericInput = e => {
    const { name, value } = e.currentTarget
    const newValue = value.length > 0 ? Number(value) : ''
    dispatchFormState({ type: UPDATE_INPUT, name, value: newValue })
  }

  const handleDateInput = (date, name) => {
    dispatchFormState({ type: UPDATE_INPUT, name, value: date })
  }

  const handleFromStorageFacility = e => {
    dispatchFormState({
      type: SET_STATE,
      state: {
        ...formState,
        fromStorageFacilityId: e.currentTarget.value,
        storageFacilityId: '',
        packageUnitInventoryId: '',
        shippingMethodId: '',
        packageUnitId: ''
      }
    })
  }

  const handleOrganizationInput = organizationId => {
    dispatchFormState({
      type: SET_STATE,
      state: {
        ...formState,
        organizationId,
        packageUnitInventoryId: '',
        fromStorageFacilityId: '',
        shippingMethodId: '',
        storageFacilityId: '',
        packageUnitId: ''
      }
    })
  }

  const filterFromStorageFacilities = () => {
    return storageFacilities.filter(
      p => p.organizationId === formState.organizationId
    )
  }

  const filterStorageFacilities = () => {
    return storageFacilities.filter(
      s =>
        s.organizationId === formState.organizationId &&
        s.id !== formState.fromStorageFacilityId
    )
  }

  const filterShippingMethods = () => {
    return shippingMethods.filter(
      p => p.storageFacility.id === formState.fromStorageFacilityId
    )
  }

  const filterPackageUnits = () => {
    return packageUnits.filter(packageUnit => {
      for (const packageUnitInventory of packageUnitInventories) {
        if (packageUnitInventory.packageUnit.id === packageUnit.id) {
          return true
        }
      }
      return false
    })
  }

  const getHeader = () => {
    if (itemId) {
      return strings.updateFormHeader
    }
    return strings.createFormHeader
  }

  return (
    <Flex
      as='form'
      axisGap={400}
      direction='column'
      className='Operations__Form'
    >
      <HeaderV2
        title={getHeader()}
        backPath={modulePath}
        buttonIcon={'save'}
        buttonText={strings.saveInventoryTransfer}
        buttonCallback={onSubmit}
      />
      <Loader isLoading={loading}>
        <Flex axisGap={800} direction={isLaptop ? 'column' : null}>
          <Flex
            axisGap={400}
            direction='column'
            className='Operations__Form__Fields'
          >
            <Box>
              <Text size={400} fontWeight={500} tone={600}>
                {strings.transfer}
              </Text>
              <OrganizationInput
                fieldName={FIELDS.organizationId}
                organizationId={formState.organizationId}
                handleInput={handleOrganizationInput}
                errors={errors}
              />
              <Flex
                axisGap={300}
                alignMainAxis='space-evenly'
                direction={isMobile ? 'column' : 'row'}
              >
                <Label style={{ flex: '1 1 0' }}>
                  <Text variant='page' tone={700}>
                    {strings.fromStorageFacility}
                  </Text>
                  <Select
                    className='Operations__Select'
                    name={FIELDS.fromStorageFacilityId}
                    value={formState.fromStorageFacilityId}
                    onChange={handleFromStorageFacility}
                  >
                    <option default value=''>
                      {strings.selectDefault}
                    </option>
                    {filterFromStorageFacilities().map(({ id, name }) => (
                      <option key={id} value={id}>
                        {name}
                      </option>
                    ))}
                  </Select>
                  <InputError error={errors?.fromStorageFacilityId} />
                </Label>
                <Label style={{ flex: '1 1 0' }}>
                  <Text variant='page' tone={700}>
                    {strings.shippingMethod}
                  </Text>
                  <Select
                    className='Operations__Select'
                    name={FIELDS.shippingMethodId}
                    value={formState.shippingMethodId}
                    onChange={handleInput}
                  >
                    <option default value=''>
                      {strings.selectDefault}
                    </option>
                    {filterShippingMethods().map(({ id, name }) => (
                      <option key={id} value={id}>
                        {name}
                      </option>
                    ))}
                  </Select>
                  <InputError error={errors?.shippingMethodId} />
                </Label>
                <Label style={{ flex: '1 1 0' }}>
                  <Flex axisGap={300} direction='column'>
                    <Text variant='page' tone={700}>
                      {strings.shippedDate}
                    </Text>
                    <DatePicker
                      maxDate={new Date()}
                      selected={formState.shippedDate}
                      className='Operations__Form__Select'
                      onChange={date =>
                        handleDateInput(date, FIELDS.shippedDate)
                      }
                    />
                    <InputError error={errors?.shippedDate} />
                  </Flex>
                </Label>
              </Flex>
              <Flex
                axisGap={300}
                alignMainAxis='space-evenly'
                direction={isMobile ? 'column' : 'row'}
              >
                <Label style={{ flex: '1' }}>
                  <Text variant='page' tone={700}>
                    {strings.toStorageFacility}
                  </Text>
                  <Select
                    className='Operations__Select'
                    name={FIELDS.storageFacilityId}
                    value={formState.storageFacilityId}
                    onChange={handleInput}
                  >
                    <option default value=''>
                      {strings.selectDefault}
                    </option>
                    {filterStorageFacilities().map(({ id, name }) => (
                      <option key={id} value={id}>
                        {name}
                      </option>
                    ))}
                  </Select>
                  <InputError error={errors?.storageFacilityId} />
                </Label>
                <Label style={{ flex: '1' }}>
                  <Flex axisGap={300} direction='column'>
                    <Text variant='page' tone={700}>
                      {strings.status}
                    </Text>
                    <Select
                      className='Operations__Select'
                      name={FIELDS.status}
                      value={formState.status}
                      onChange={handleInput}
                    >
                      <option default value=''>
                        {strings.selectDefault}
                      </option>
                      {Object.entries(inventoryTransferStatuses).map(
                        ([id, name]) => (
                          <option key={id} value={id}>
                            {name}
                          </option>
                        )
                      )}
                    </Select>
                    <InputError error={errors?.status} />
                  </Flex>
                </Label>
                <Label style={{ flex: '1' }}>
                  <Flex axisGap={300} direction='column'>
                    <Text variant='page' tone={700}>
                      {strings.deliveredDate}
                    </Text>
                    <DatePicker
                      maxDate={new Date()}
                      selected={formState.deliveredDate}
                      className='Operations__Form__Select'
                      onChange={date =>
                        handleDateInput(date, FIELDS.deliveredDate)
                      }
                    />
                    <InputError error={errors?.deliveredDate} />
                  </Flex>
                </Label>
              </Flex>
            </Box>
            <Box>
              <Text size={400} fontWeight={500} tone={600}>
                {strings.packaging}
              </Text>
              <Flex
                axisGap={300}
                alignMainAxis='space-evenly'
                direction={isMobile ? 'column' : 'row'}
              >
                <Label style={{ flex: '1 1 0' }}>
                  <Text variant='page' tone={700}>
                    {strings.packageUnit}
                  </Text>
                  <Select
                    className='Operations__Select'
                    name={FIELDS.packageUnitId}
                    value={formState.packageUnitId}
                    onChange={handleInput}
                  >
                    <option default value=''>
                      {strings.selectDefault}
                    </option>
                    {filterPackageUnits().map(({ id, name }) => (
                      <option key={id} value={id}>
                        {name}
                      </option>
                    ))}
                  </Select>
                  <InputError error={errors?.packageUnitId} />
                </Label>
                <Label style={{ flex: '1 1 0' }}>
                  <Text variant='page' tone={700}>
                    {strings.quantity}
                  </Text>
                  <Input
                    className='Operations__Input'
                    type='number'
                    name={FIELDS.quantity}
                    value={formState.quantity}
                    onChange={handleNumericInput}
                  />
                  <InputError error={errors?.quantity} />
                </Label>
              </Flex>
            </Box>
          </Flex>
        </Flex>
      </Loader>
    </Flex>
  )
}

export default PackageUnitInventoryTransferForm
